// src/pages/admin/FuncoesLimites.js
import React, { useEffect, useMemo, useState } from "react";
import { useTable, useFilters, useSortBy } from "react-table";

import api, { handleApi } from "pages/api/api";
import { useAlert } from "pages/context/AlertContext";
import { useAuth } from "pages/context/AuthContext";
import PageHeader from "pages/components/PageHeader";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

import {
  Card,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Checkbox,
  FormControlLabel,
  IconButton,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";

// ---------------- Endpoints (existentes na tua API) ----------------
const EP = {
  FL_Sel: "FuncoesLimites/SelLimites",
  FL_Upd: "FuncoesLimites/UpdLimite", // upsert por idfuncao
  FUN_Sel: "Funcoes/SelFuncoes",
};

// normalizador para apanhar "Distribuição" / "Distribuicao" / "Distrib."
const norm = (s) =>
  (s || "")
    .toString()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .trim();

// ---------------- Filtros ----------------
function TextFilter({ column: { filterValue, setFilter } }) {
  return (
    <input
      value={filterValue || ""}
      onChange={(e) => setFilter(e.target.value || undefined)}
      placeholder="filtrar…"
      style={{
        width: "100%",
        padding: "6px 4px",
        border: "none",
        borderBottom: "1px solid rgba(0,0,0,0.2)",
        outline: "none",
        background: "transparent",
        fontSize: 12,
        color: "#556",
      }}
      onFocus={(e) => (e.target.style.borderBottom = "1px solid rgba(0,0,0,0.35)")}
      onBlur={(e) => (e.target.style.borderBottom = "1px solid rgba(0,0,0,0.2)")}
    />
  );
}

// ---------------- Persistência de filtros ----------------
const FILTERS_KEY = "tbl:funcoeslimites:filters";
const KEEP_KEY = "tbl:funcoeslimites:keep";

export default function FuncoesLimites() {
  const { showAlert } = useAlert();
  const { temPermissao } = useAuth();

  const [dados, setDados] = useState([]);
  const [loading, setLoading] = useState(false);

  // lista de funções para o <select>
  const [funcoes, setFuncoes] = useState([]); // [{id, designacao}]

  // “Manter filtros” – por defeito TRUE
  const initialKeep =
    (() => {
      try {
        const v = localStorage.getItem(KEEP_KEY);
        return v === null ? true : JSON.parse(v);
      } catch {
        return true;
      }
    })() ?? true;
  const [keepFilters, setKeepFilters] = useState(initialKeep);

  const initialFilters = keepFilters
    ? (() => {
        try {
          return JSON.parse(localStorage.getItem(FILTERS_KEY) || "[]");
        } catch {
          return [];
        }
      })()
    : [];

  // dialog inserir/editar
  const [dlg, setDlg] = useState({
    open: false,
    edit: false,
    reg: {
      idfuncao: "",
      max_ausentes: "",
    },
  });

  const podeW = temPermissao("Menu Administração", "w");
  const podeU = temPermissao("Menu Administração", "u");

  // ---------- Helpers ----------
  const funLabel = (id) => {
    const f = funcoes.find((x) => Number(x.id) === Number(id));
    return f ? f.designacao : id ?? "";
  };

  // ---------- Carregar ----------
  async function carregar() {
    setLoading(true);
    try {
      const [rows] = await handleApi(api.post(EP.FL_Sel));
      setDados(Array.isArray(rows) ? rows : []);
    } catch {
      showAlert("Falha ao carregar limites.", "error");
    } finally {
      setLoading(false);
    }
  }

  async function carregarFuncoes() {
    try {
      const [rows] = await handleApi(api.post(EP.FUN_Sel));
      const list = Array.isArray(rows) ? rows : [];
      const funcoesFiltradas = list
        .map((x) => ({ id: x.id, designacao: x.designacao }))
        .filter((x) => x.id && x.designacao);

      setFuncoes(funcoesFiltradas);

      // Se estiveres a inserir (não editar), pré-seleciona “Distribuição”
      if (!dlg.edit && (!dlg.reg.idfuncao || dlg.reg.idfuncao === "")) {
        const dist = funcoesFiltradas.find((f) =>
          (f.designacao || "").toLowerCase().startsWith("distrib")
        );
        if (dist) {
          setDlg((d) => ({ ...d, reg: { ...d.reg, idfuncao: dist.id } }));
        }
      }
    } catch {
      setFuncoes([]);
    }
  }

  useEffect(() => {
    carregar();
    carregarFuncoes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (
      dlg.open && // diálogo aberto
      !dlg.edit && // modo inserir
      (!dlg.reg.idfuncao || dlg.reg.idfuncao === "") &&
      funcoes.length
    ) {
      const dist = funcoes.find((f) => {
        const n = norm(f.designacao);
        return n.startsWith("distrib"); // "distribuição", "distribuicao", "distrib."
      });
      if (dist) {
        setDlg((d) => ({ ...d, reg: { ...d.reg, idfuncao: dist.id } }));
      } else {
        // fallback opcional: primeira função
        setDlg((d) => ({ ...d, reg: { ...d.reg, idfuncao: funcoes[0].id } }));
      }
    }
  }, [dlg.open, dlg.edit, dlg.reg.idfuncao, funcoes]);

  // ---------- Tabela ----------
  const columns = useMemo(
    () => [
      {
        Header: "",
        id: "acoes",
        disableFilters: true,
        width: 72,
        minWidth: 56,
        maxWidth: 90,
        Cell: ({ row }) => {
          const a = row.original;
          return (
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <IconButton
                color="warning"
                size="small"
                disabled={!podeU}
                onClick={() => abrirEditar(a)}
                title="Editar"
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </div>
          );
        },
      },
      {
        Header: "Função",
        accessor: "designacao", // já vem do SelLimites
        id: "designacao",
        Filter: TextFilter,
      },
      {
        Header: "Limite/dia",
        accessor: (r) => String(r.max_ausentes ?? ""),
        id: "max_ausentes",
        Filter: TextFilter,
      },
    ],
    [podeU]
  );

  const table = useTable(
    {
      columns,
      data: dados,
      initialState: { filters: initialFilters },
      autoResetFilters: false,
      autoResetSortBy: false,
    },
    useFilters,
    useSortBy
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow, setAllFilters, state } =
    table;

  // persistência filtros
  useEffect(() => {
    if (!keepFilters && (state.filters?.length || 0) > 0) {
      setAllFilters([]);
      try {
        localStorage.removeItem(FILTERS_KEY);
      } catch {}
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dados]);

  useEffect(() => {
    try {
      localStorage.setItem(KEEP_KEY, JSON.stringify(keepFilters));
    } catch {}
    if (!keepFilters) {
      setAllFilters([]);
      try {
        localStorage.removeItem(FILTERS_KEY);
      } catch {}
    }
  }, [keepFilters, setAllFilters]);

  useEffect(() => {
    if (!keepFilters) return;
    try {
      localStorage.setItem(FILTERS_KEY, JSON.stringify(state.filters || []));
    } catch {}
  }, [state.filters, keepFilters]);

  // ---------- Ações ----------
  const abrirNovo = () =>
    setDlg({
      open: true,
      edit: false,
      reg: { idfuncao: "", max_ausentes: "" },
    });

  const abrirEditar = (r) =>
    setDlg({
      open: true,
      edit: true,
      reg: {
        idfuncao: r.idfuncao,
        max_ausentes: r.max_ausentes ?? "",
      },
    });

  const fecharDlg = () => setDlg((d) => ({ ...d, open: false }));

  const gravar = async () => {
    const b = dlg.reg;
    if (!b.idfuncao || b.max_ausentes === "" || b.max_ausentes === null) {
      showAlert("Indica a Função e o Limite/dia.", "warning");
      return;
    }
    const payload = {
      idfuncao: Number(b.idfuncao),
      max_ausentes: Number(b.max_ausentes),
    };
    const [_, err, meta] = await handleApi(api.post(EP.FL_Upd, payload));
    if (err) return showAlert(err, "error");
    if (meta?.message) showAlert(meta.message, meta.severity || "success");
    fecharDlg();
    await carregar();
  };

  // ---------- Render ----------
  return (
    <DashboardLayout>
      <DashboardNavbar />
      <PageHeader />

      <MDBox pt={1}>
        <Card>
          <MDBox p={3}>
            <MDBox display="flex" alignItems="center" justifyContent="space-between" mb={2} gap={2}>
              <MDTypography variant="h6">Registos</MDTypography>

              <MDBox display="flex" alignItems="center" gap={2} flexWrap="wrap">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={keepFilters}
                      onChange={(e) => setKeepFilters(e.target.checked)}
                      size="small"
                    />
                  }
                  label="Manter filtros"
                />
                <MDButton
                  color="success"
                  startIcon={<AddIcon />}
                  onClick={abrirNovo}
                  disabled={!podeW}
                >
                  Novo limite
                </MDButton>
              </MDBox>
            </MDBox>

            <MDBox sx={{ height: "68vh", overflow: "auto", borderRadius: 1 }}>
              <table {...getTableProps()} style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  {headerGroups.map((hg) => (
                    <tr {...hg.getHeaderGroupProps()}>
                      {hg.headers.map((col) => (
                        <th
                          {...col.getHeaderProps(col.getSortByToggleProps())}
                          style={{
                            width: col.id === "acoes" ? 72 : undefined,
                            position: "sticky",
                            top: 0,
                            zIndex: 2,
                            background: "#fff",
                            textAlign: "left",
                            padding: "6px 8px",
                            fontSize: 12,
                            color: "#7b809a",
                            verticalAlign: "bottom",
                            boxShadow: "0 2px 4px rgba(0,0,0,0.06)",
                          }}
                        >
                          <div style={{ display: "flex", alignItems: "center" }}>
                            {col.render("Header")}
                            {col.isSorted ? (
                              col.isSortedDesc ? (
                                <span style={{ marginLeft: 6 }}>▼</span>
                              ) : (
                                <span style={{ marginLeft: 6 }}>▲</span>
                              )
                            ) : null}
                          </div>
                          {!col.disableFilters && (
                            <div onClick={(e) => e.stopPropagation()} style={{ marginTop: 4 }}>
                              {col.canFilter ? col.render("Filter") : null}
                            </div>
                          )}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody {...getTableBodyProps()}>
                  {rows.map((row) => {
                    prepareRow(row);
                    return (
                      <tr {...row.getRowProps()} style={{ borderBottom: "1px solid #f0f2f5" }}>
                        {row.cells.map((cell) => (
                          <td
                            {...cell.getCellProps()}
                            style={{
                              width: cell.column.id === "acoes" ? 72 : undefined,
                              whiteSpace: "nowrap",
                              padding: "10px 8px",
                              fontSize: 12,
                              color: "#344767",
                            }}
                          >
                            {cell.render("Cell")}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </MDBox>
          </MDBox>
        </Card>
      </MDBox>

      {/* Dialog Inserir/Editar */}
      <Dialog open={dlg.open} onClose={fecharDlg} fullWidth maxWidth="sm">
        <DialogTitle>{dlg.edit ? "Editar limite" : "Novo limite"}</DialogTitle>
        <DialogContent dividers>
          <MDBox display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
            <MDBox gridColumn="1 / span 2">
              <label>Função</label>
              {/* Preferência: <select> estilizado */}
              <select
                value={dlg.reg.idfuncao}
                onChange={(e) =>
                  setDlg((d) => ({ ...d, reg: { ...d.reg, idfuncao: e.target.value } }))
                }
                style={{
                  width: "100%",
                  padding: 12,
                  borderRadius: 6,
                  border: "1px solid #ced4da",
                  fontSize: 14,
                }}
              >
                <option value="">— escolher —</option>
                {funcoes.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.designacao}
                  </option>
                ))}
              </select>
            </MDBox>

            <MDBox>
              <label>Limite por dia</label>
              <TextField
                type="number"
                fullWidth
                value={dlg.reg.max_ausentes}
                onChange={(e) =>
                  setDlg((d) => ({ ...d, reg: { ...d.reg, max_ausentes: e.target.value } }))
                }
              />
            </MDBox>
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={fecharDlg} color="secondary">
            Fechar
          </MDButton>
          <MDButton onClick={gravar} color="success" disabled={!podeW && !dlg.edit}>
            {dlg.edit ? "Gravar" : "Inserir"}
          </MDButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}
